import { ChangeDetectorRef, Component, ComponentRef, TemplateRef, ViewChild } from "@angular/core";
import { ListDetailsPanelComponent } from "../../../list-details-panel/list-details-panel.component";
import { ItemSelectorPanelComponent } from "../../../item-selector-panel/item-selector-panel.component";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Permission, PermissionMetaData } from "../../../../models/permission";
import { ApiService } from "../../../../services/api.service";
import { LoadingService } from "../../../../services/loading.service";
import { StaticTableComponent } from "../../../static-table/static-table.component";
import PermissionTableMeta from "./config/permissionTableMeta.json";
import { ActivatedRoute, Router } from "@angular/router";
import { ToasterService } from "../../../../services/toaster.service";
import { NgClass, NgIf } from "@angular/common";

@Component({
  selector: "app-manage-user-roles-permissions",
  standalone: true,
  imports: [
    ListDetailsPanelComponent,
    ItemSelectorPanelComponent,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    NgIf
  ],
  templateUrl: "./manage-user-roles-permissions.component.html",
  styleUrl: "./manage-user-roles-permissions.component.scss"
})
export class ManageUserRolesPermissionsComponent {
  rolesForm!: FormGroup;
  readOnlyMode: any;
  editingEnabled: any;
  permissionsParentChildMap: Map<string, Permission[]> = new Map<string, Permission[]>();
  selectedPermissionsParentChildMap: Map<string, Permission[]> = new Map<string, Permission[]>();
  leftPanelData: { id: string; name: string }[] = [];
  roleId: string = "";
  searchTerm: string = "";
  focusSearch: boolean = false;
  openedPermissionId: string = "";
  staticTableInstance: StaticTableComponent | undefined;

  @ViewChild("itemSelectorComponent", { static: false }) itemSelectorComponent?: ItemSelectorPanelComponent;
  @ViewChild("searchBar") searchBarTemplate!: TemplateRef<any>;


  constructor(private formBuilder: FormBuilder,
              private apiService: ApiService,
              private loadingService: LoadingService,
              private cdr: ChangeDetectorRef,
              private router: Router,
              private route: ActivatedRoute,
              private toasterService: ToasterService) {
    this.rolesForm = this.formBuilder.group({
      name: ["", Validators.required],
      description: [""]
    });
  }

  ngOnInit() {
    this.setUpAppMode();
  }

  goBack() {
    this.router.navigateByUrl("/admin/manage-users", {
      state: { selectedTab: "roles" }
    });
  }

  reset() {

  }

  save() {
    this.rolesForm.markAllAsTouched()
    if(this.rolesForm.invalid) {
      return;
    }
    this.loadingService.show();
    const selectedPermissionArray = Array.from(this.selectedPermissionsParentChildMap.values()).flat();

    const rolesData = {
      ...this.rolesForm.value,
      active: true,
      permissionIds: selectedPermissionArray.map((d: any) => d?.id)
    };
    if (this.editingEnabled) {
      this.callApiToUpdateRole(rolesData);
    } else {
      this.callApiToSaveRole(rolesData);
    }
  }

  private callApiToSaveRole(rolesData: any) {
    this.apiService.addRole(rolesData).subscribe({
      next: (res: any) => {
        this.goBack();
        this.loadingService.hide();
        this.toasterService.success("Role created successfully");
      },
      error: err => {
        this.loadingService.hide();
        this.toasterService.error(err.error.message);
      }
    });
  }

  private callApiToUpdateRole(rolesData: any) {
    this.apiService.updateRole(rolesData, this.roleId).subscribe({
      next: (res: any) => {
        this.goBack();
        this.loadingService.hide();
        this.toasterService.success("Role updated successfully");
      },
      error: err => {
        this.loadingService.hide();
        this.toasterService.error(err.error.message);
      }
    });
  }

  private getPermissionsList(): Promise<void> {
    this.loadingService.show();

    return new Promise((resolve, reject) => {
      this.apiService.getPermissionsList().subscribe({
        next: (res: any) => {
          const permissionsList: Permission[] = res?.data?.records ?? [];
          this.permissionsParentChildMap.clear();

          for (const permission of permissionsList) {
            const resourceType = permission?.metadata?.resourceType ?? "Others";
            if (!this.permissionsParentChildMap.has(resourceType)) {
              this.permissionsParentChildMap.set(resourceType, []);
            }
            this.permissionsParentChildMap.get(resourceType)?.push(permission);
          }

          console.log(this.permissionsParentChildMap);
          this.populateLeftPanelDataList(this.permissionsParentChildMap.keys());
          this.loadingService.hide();
          resolve();
        },
        error: (error: any) => {
          console.error("Error fetching permissions list:", error?.error?.errorDesc ?? error.message);
          this.loadingService.hide();
          reject(error);
        }
      });
    });
  }


  private populateLeftPanelDataList(data: IterableIterator<string>) {
    const dataArray = Array.from(data);
    this.leftPanelData = dataArray.map(item => {
      return {
        id: item,
        name: item
      };
    });
  }

  handlePermissionItemSelection(event: { item: any; index: number }) {
    this.searchTerm = ""
    this.openedPermissionId = event.item.id;
    const componentsToLoad: { component: any, data?: any }[] = [
      {
        component: this.searchBarTemplate,
        data: { permissionName: event.item.name }
      },
      {
        component: StaticTableComponent,
        data: {
          data: this.permissionsParentChildMap.get(this.openedPermissionId),
          tableMeta: PermissionTableMeta,
          selectedItems: this.selectedPermissionsParentChildMap.get(this.openedPermissionId) ?? [],
          deleteAllowed: false,
          showSerialNumber: false,
          readOnlyMode: this.readOnlyMode
        }
      }];
    const componentRefs = this.itemSelectorComponent?.loadComponents(componentsToLoad);
    componentRefs?.forEach(componentRef => {
      if (componentRef?.instance instanceof StaticTableComponent) {
        this.staticTableInstance = componentRef.instance;
        this.staticTableInstance.selectedItemsChange.subscribe((items: Permission[]) => {
          this.selectedPermissionsParentChildMap.set(this.openedPermissionId, items);
        });
      }

    });
  }

  private setUpAppMode() {
    this.roleId = this.route.snapshot.paramMap.get("roleId") || "";
    if (this.roleId) {
      const path = this.route.snapshot.url[1]?.path || "";
      this.editingEnabled = path === "edit";
      this.readOnlyMode = path === "view";
      this.patchRoleValue();
    } else {
      this.getPermissionsList().then(r => {
        this.editingEnabled = false;
        this.readOnlyMode = false;
      });
    }
  }

  private patchRoleValue() {
    this.loadingService.show();

    this.apiService.getRoleById(this.roleId, true).subscribe({
      next: async (res: any) => {
        await this.getPermissionsList(); // get all permissions first

        const roleData = res.data;
        const selectedPermissions: Permission[] = roleData?.permission ?? [];

        // Reset and populate selectedPermissionsParentChildMap
        this.selectedPermissionsParentChildMap.clear();

        selectedPermissions.forEach(permission => {
          const resourceType = permission?.metadata?.resourceType ?? "Others";
          if (!this.selectedPermissionsParentChildMap.has(resourceType)) {
            this.selectedPermissionsParentChildMap.set(resourceType, []);
          }

          const existing = this.selectedPermissionsParentChildMap.get(resourceType);
          if (existing && !existing.find(p => p.id === permission.id)) {
            existing.push(permission);
          }
        });

        this.rolesForm.patchValue(roleData);
        if (this.readOnlyMode) this.rolesForm.disable();

        this.loadingService.hide();
      },
      error: (err) => {
        this.loadingService.hide();
        this.toasterService.error(err.error.message);
      }
    });
  }

  handleTableSearch(event: KeyboardEvent): void {
    const input = this.searchTerm.toLowerCase().trim();

    const originalPermissionList = this.permissionsParentChildMap.get(this.openedPermissionId || "") || [];

    const filteredPermissionList = originalPermissionList.filter(item => {
      const name = item.displayName?.toLowerCase() ?? "";
      const description = item.description?.toLowerCase() ?? "";
      return name.includes(input) || description.includes(input);
    });

    if (this.staticTableInstance) {
      this.staticTableInstance.data = filteredPermissionList
      this.staticTableInstance.ngOnChanges({
        data: {
          currentValue: filteredPermissionList,
          previousValue: [],
          firstChange: false,
          isFirstChange: () => false
        }
      });
    }
  }

}
